/**
 * When does the actor's local day next roll over?
 *
 * Mood Check's "hide for today" expires at the actor's local midnight — a
 * boundary the SERVER owns, derived from their stored Player AI time zone
 * (ADR-0054 §8, PD1). A continuously-open Home therefore has to wake at that
 * instant, not on a polling interval: a poll either fires too often (cost for
 * nothing) or leaves the widget hidden into the next day (the bug it exists to
 * prevent).
 *
 * Deliberately NOT a date library. `Intl.DateTimeFormat` already knows every
 * zone the platform does, which is the same source `ZoneInfo` reads
 * server-side; adding a dependency to re-derive it would be a second answer to
 * a question that already has one.
 */

/** The wall-clock date in `timeZone`, as `YYYY-MM-DD`. */
export function localDateIn(timeZone: string, now: Date): string {
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = dateFormatter(timeZone);
  } catch {
    formatter = dateFormatter("UTC");
  }
  return formatter.format(now);
}

function dateFormatter(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** Find the next local date boundary, including short and long DST days. */
export function msUntilNextLocalDay(
  timeZone: string,
  now: Date = new Date(),
): number {
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = dateFormatter(timeZone);
  } catch {
    formatter = dateFormatter("UTC");
  }
  const start = now.getTime();
  const today = formatter.format(now);
  // Search actual instants rather than assuming a local day lasts 24 hours.
  let low = start;
  let high = start + 48 * 60 * 60 * 1_000;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (formatter.format(new Date(middle)) === today) low = middle;
    else high = middle;
  }
  // Cross the boundary with one second of scheduling slack.
  return Math.max(1_000, high - start + 1_000);
}

/**
 * `setTimeout`'s delay is a signed 32-bit int; anything larger fires
 * immediately. A day never exceeds it, but a corrupt zone could, so the
 * scheduler clamps rather than trusting the arithmetic.
 */
export const MAX_TIMEOUT_MS = 2_147_483_647;
